import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { produce } from 'immer';
import { uniq, isEqual } from 'lodash';
import { usePatientGroup } from '../patient-group';
import { useChats } from '../chats';
import { IAlert, useAlertCount, useGetStaleAlerts } from '../../libs/alert';
import { IContact } from '../../libs/contact';
import usePrevious from '../../libs/hooks/usePrevious';
import Toast from '../../components/organisms/toast';
import {
  DEFAULT_TRIAGE_FILTERS,
  useTriageData,
  addTriageEntry,
  extractContacts,
  createTriageData,
  GetTriagePatientData,
  updateTriageEntry,
  updateStaleAlerts,
  getStaleAlertsData,
  getLoadingCategories,
  addOptionsToURL,
  parseTriageUrl,
  stringifyOptions,
  updateSnoozedAlerts,
  ShouldAddToPaginatedResult,
  determineMissingChatThreads,
  getAndAddMissingThreads
} from './utils';
import {
  TriageValue,
  TriageStats,
  TriageParamsOptions,
  TriagedPatient,
  TriageLoadingKey,
  TriageCategory
} from './types';
import { useUrlState } from '../active-dialogs/url';
import { TriageUpdaterContext } from './updater';
import { IPatient } from '../../libs/patient';

const TriageContext = React.createContext<TriageValue>({
  data: null,
  triageInProgress: false,
  setTriageData: null,
  addTriagedPatient: null,
  staleAlertsData: null,
  options: null,
  updateOptions: null,
  patientId: null,
  updateStaleAlertsPage: null,
  isLoading: true,
  loadingCategories: null,
  isCategoryLoading: null,
  isStaleAlertsLoading: false,
  isPendingPGEnterprise: false,
  error: null,
  stats: null,
  getContact: null,
  handleResolveAlert: null,
  handleToggleSnoozeAlert: null,
  handleToggleSnoozePatient: null,
  resetToDashboard: null
});

export const TriageProvider: React.FC = ({ children }) => {
  const { isViewingPatientFromUrl } = useUrlState();
  // Extract options information from the query string
  const navigate = useNavigate();
  const location = useLocation();
  const search = location.search;
  const initialOptions = parseTriageUrl(search);
  // Set up the triage options
  const [options, setOptions] = useState(initialOptions);
  const skipNextUrlSyncRef = useRef(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const prevOptions = usePrevious(options);
  // Fetch the current patient group
  const {
    isLoading: pgLoading,
    isEnterprise,
    getActiveGroup
  } = usePatientGroup();
  // Use undefined here as a way to differentiate between
  // null, which is the 'All Patients View' and undefined,
  // which is 'not done fetching yet'
  const activePatientGroup = !pgLoading ? getActiveGroup() : undefined;
  const prevPatientGroup = usePrevious(activePatientGroup);
  const isPendingPGEnterprise =
    !pgLoading &&
    isEnterprise &&
    (!activePatientGroup || activePatientGroup?.id === -1);
  // Keep options in sync with URL
  useEffect(() => {
    if (skipNextUrlSyncRef.current) {
      skipNextUrlSyncRef.current = false;
      return;
    }
    const urlOptions = parseTriageUrl(location.search);
    const urlStr = stringifyOptions(urlOptions) || '';
    const base = options || DEFAULT_TRIAGE_FILTERS;
    const optStr = stringifyOptions(base) || '';
    if (urlStr !== optStr) {
      setOptions(urlOptions);
      setRefreshTick((n) => n + 1);
    }
  }, [location.search]);
  // When patient group switches, reset the options
  useEffect(() => {
    // Don't consider resetting because we don't have an active
    // patient group loaded yet
    if (activePatientGroup === undefined) {
      return;
    }
    // We're not concerned with a transition from
    // undefined --> active patient group
    if (prevPatientGroup === undefined) {
      return;
    }
    // active patient group --> different active patient group
    if (prevPatientGroup?.id !== activePatientGroup?.id) {
      navigate(location.pathname);
      setOptions(DEFAULT_TRIAGE_FILTERS);
    }
  }, [activePatientGroup]);
  const hasTempFilter = !!options._tempAppFilter;
  useEffect(() => {
    if (!pgLoading && prevPatientGroup === undefined && hasTempFilter) {
      setOptions((currentOptions) => ({ ...currentOptions }));
    }
  }, [pgLoading, prevPatientGroup, hasTempFilter]);

  const isPatientDialogOpenFromSearch = search?.includes('patientId');
  let isTriageDataFetchDisabledBase;
  if (isViewingPatientFromUrl && isPatientDialogOpenFromSearch) {
    isTriageDataFetchDisabledBase = true;
  } else {
    isTriageDataFetchDisabledBase = pgLoading && !hasTempFilter;
  }

  let isTriageDataFetchDisabled = isTriageDataFetchDisabledBase;
  if (!isTriageDataFetchDisabledBase && isPendingPGEnterprise) {
    isTriageDataFetchDisabled = true;
  }

  const {
    data: triageData,
    dispatch,
    setData: setTriageData,
    isLoading,
    loadingState,
    error
  } = useTriageData(
    activePatientGroup,
    prevPatientGroup,
    options,
    prevOptions,
    isTriageDataFetchDisabled,
    refreshTick
  );
  const pgOrTriageLoading = pgLoading || isLoading;
  const isAlertCountLoading = pgLoading || triageData == null;

  // When closing a URL-driven patient dialog, only update options if they actually changed
  const prevIsViewingPatientFromUrl = useRef(isViewingPatientFromUrl);
  useEffect(() => {
    if (prevIsViewingPatientFromUrl.current && !isViewingPatientFromUrl) {
      const urlOptions = parseTriageUrl(location.search);
      const hydrated = hasTempFilter
        ? { ...urlOptions, _tempAppFilter: options._tempAppFilter }
        : urlOptions;

      // Determine if meaningful filter changes occurred (beyond patientId)
      const prevStr = stringifyOptions(options) || '';
      const nextStr = stringifyOptions(hydrated) || '';
      const onlyPatientIdChanged =
        prevStr.replace(/&?patientId=\d+/, '') ===
        nextStr.replace(/&?patientId=\d+/, '');

      // Only update if options actually changed
      if (!isEqual(options, hydrated)) {
        if (onlyPatientIdChanged) {
          // Suppress URL sync & refetch when only patientId changed
          skipNextUrlSyncRef.current = true;
          setOptions(hydrated);
        } else {
          // Meaningful filter changes occurred, update normally
          updateOptions(hydrated);
        }
      }
      // If options didn't change, do nothing
    }
    prevIsViewingPatientFromUrl.current = isViewingPatientFromUrl;
  }, [isViewingPatientFromUrl]);

  const { data: totalAlerts, setData: setTotalAlerts } = useAlertCount(
    activePatientGroup,
    triageData,
    isAlertCountLoading,
    'unreadOnly'
  );
  const { data: totalSnoozedAlerts, setData: setTotalSnoozedAlerts } =
    useAlertCount(
      activePatientGroup,
      triageData,
      isAlertCountLoading,
      'snoozedOnly'
    );
  const {
    data: staleAlertsData,
    page: staleAlertsPage,
    setPage: setStaleAlertsPage,
    isLoading: isStaleAlertsLoading
  } = useGetStaleAlerts(triageData, pgOrTriageLoading);
  // Keep stale alerts page in sync with options from URL
  useEffect(() => {
    const desiredPage = options?.staleAlerts?.page ?? 0;
    if (desiredPage !== staleAlertsPage) {
      setStaleAlertsPage(desiredPage);
    }
  }, [options?.staleAlerts?.page]);
  const [stats, setStats] = useState<TriageStats>({
    totalAlerts: 0,
    totalSnoozedAlerts: 0,
    totalStaleAlerts: 0,
    triageAlerts: 0,
    danger: 0,
    warning: 0,
    success: 0,
    unresponsive: 0,
    inactive: 0,
    completed: 0,
    snoozed: 0,
    paused: 0,
    recent: 0,
    stun: 0
  });

  /**
   * Update triage options state
   *
   * @param opts triage params options (aka triage filters)
   */
  const updateOptions = (opts: TriageParamsOptions) => {
    const updatedURL = addOptionsToURL(location, opts);
    skipNextUrlSyncRef.current = true;
    navigate(updatedURL);
    const hasChanged = !isEqual(options, opts);
    if (hasChanged) {
      setOptions(opts);
      return;
    }
    // Keep a manual refetch path when the caller re-applies identical options.
    skipNextUrlSyncRef.current = false;
    setRefreshTick((n) => n + 1);
  };

  /**
   * Apply a temporary app filter to triage
   *
   * @param key the app key to filter for
   */
  const updateTempAppFilter = (key: string) => {
    const newOptions = produce(options, (draft) => {
      const appFilter = key != null && key ? key : undefined;
      const hasChanged = draft._tempAppFilter !== key;
      draft._tempAppFilter = appFilter;
      // Reset all the pages to be 0 if app filter is changed
      if (hasChanged) {
        draft.alerts.page = 0;
        draft.snoozedAlerts.page = 0;
        for (const category of Object.keys(draft.patients)) {
          draft.patients[category].page = 0;
        }
        // Remind the user that the filter is temporary
        if (appFilter) {
          Toast({
            intent: 'primary',
            message: 'A temporary program filter is applied!'
          });
        }
      }
    });
    updateOptions(newOptions);
  };

  /**
   * Add a single triage entry to the greater context
   *
   * @param data the patient triage entry
   */
  const addTriagedPatient = (data: TriagedPatient, contacts?: IContact[]) => {
    let newTriageData = addTriageEntry(triageData, data);
    if (contacts?.length > 0) {
      newTriageData = extractContacts(newTriageData, contacts);
    }
    setTriageData(newTriageData);
  };

  /**
   * Return the contact from the triage data by ID
   *
   * @param id the id of the contact
   * @returns the contact if matched, or null
   */
  const getContact = (id: number): IContact => {
    const contact = triageData?.contacts?.[id];
    return contact;
  };

  // Propergate the stale alert data into the triage context storage
  useEffect(() => {
    // When the triage data is not set, create an empty shell
    if (!triageData && !isLoading) {
      setTriageData(createTriageData());
    }
    // Insert the stale alerts to the context storage
    if (staleAlertsData && triageData) {
      setTriageData(updateStaleAlerts(triageData, staleAlertsData));
    }
  }, [staleAlertsData, triageData]);

  /**
   * Rectify the effect of snoozing the alerts (alert cannot be unsnoozed)
   *
   * IMPORANT: if alert is freshly snoozed, the _justSnoozed flag must be set
   *
   * @param alerts the alerts that have been snoozed
   */
  const handleToggleSnoozeAlert = (
    alerts: IAlert[],
    hasUnsnoozed = false,
    stunAlertIds?: IAlert['id'][]
  ): void => {
    if (!alerts || !alerts.length) {
      return;
    }
    let newTotalSnoozedAlerts: number = null;
    let newTotalAlerts: number = null;
    let patientIds: number[] = [];
    for (const alert of alerts) {
      const entry = triageData.triages[alert.patientId];
      const shouldAddPatientId =
        entry &&
        entry.alertIds.includes(alert.id) &&
        (alert._justSnoozed || hasUnsnoozed);
      if (shouldAddPatientId) {
        patientIds.push(alert.patientId);
      }
    }
    // Refetch the relevant entries
    patientIds = uniq(patientIds);
    const promises: Promise<TriagedPatient>[] = [];
    for (const patientId of patientIds) {
      promises.push(GetTriagePatientData(patientId));
    }
    Promise.allSettled(promises).then((results) => {
      // Rectify the state
      const newTriageData = produce(triageData, (draft) => {
        const result = updateSnoozedAlerts(
          draft,
          alerts,
          totalAlerts,
          totalSnoozedAlerts,
          options.alerts.order,
          hasUnsnoozed,
          stunAlertIds
        );
        newTotalAlerts = result.newTotalAlerts;
        newTotalSnoozedAlerts = result.newTotalSnoozedAlerts;
        // Update the relevant triage entries to the new category
        for (const result of results) {
          if (result.status === 'fulfilled') {
            const entry = result.value;
            updateTriageEntry(draft, entry);
          }
        }
      });
      if (newTotalAlerts !== null) {
        setTotalAlerts(newTotalAlerts);
      }
      if (newTotalSnoozedAlerts !== null) {
        setTotalSnoozedAlerts(newTotalSnoozedAlerts);
      }
      setTriageData(newTriageData);
    });
    const snoozeWord = hasUnsnoozed ? 'unsnoozed' : 'snoozed';
    Toast({
      intent: 'success',
      message: `The ${
        alerts.length === 1 ? 'alert was' : 'alerts were'
      } ${snoozeWord} successfully!`
    });
  };

  /**
   * Rectify the effect of snoozing the patient
   *
   * IMPORTANT: if patient is freshly snoozed, the _justSnoozed flag must be set
   *
   * @param patient the patient that has been snoozed
   */
  const handleToggleSnoozePatient = (
    patient: IPatient,
    alerts: IAlert[],
    hasUnsnoozed = false
  ) => {
    const patientId = patient.id;
    let newTotalAlerts = null;
    let newTotalSnoozedAlerts = null;
    const newTriageData = produce(triageData, (draft) => {
      // Update the patient
      draft.patients[patient.id] = patient;
      // Locate the old triage entry
      const entry = draft.triages[patientId];
      if (hasUnsnoozed) {
        if (draft.pages?.snoozed) {
          if (draft.pages.snoozed.entityIds.includes(patientId)) {
            draft.pages.snoozed.entityIds =
              draft.pages.snoozed.entityIds.filter((id) => id !== patientId);
          }
          draft.pages.snoozed.total =
            draft.pages.snoozed.total === 0 ? 0 : draft.pages.snoozed.total - 1;
        }
      } else {
        // Make sure it's not currently snoozed
        const isTriageEntrySnoozed =
          entry.primaryCategoryId === TriageCategory.Snoozed ||
          entry.secondaryCategoryIds.includes(TriageCategory.Snoozed);
        if (!isTriageEntrySnoozed) {
          if (draft.pages?.snoozed) {
            if (!draft.pages.snoozed.entityIds.includes(patientId)) {
              if (ShouldAddToPaginatedResult(draft.pages.snoozed)) {
                draft.pages.snoozed.entityIds.push(patientId);
              }
            }
            draft.pages.snoozed.total = draft.pages.snoozed.total + 1;
          }
          // Add to the dual category
          entry.secondaryCategoryIds.push(TriageCategory.Snoozed);
        }
      }
      // Handle alerts
      const result = updateSnoozedAlerts(
        draft,
        alerts,
        totalAlerts,
        totalSnoozedAlerts,
        options.alerts.order,
        hasUnsnoozed,
        []
      );
      newTotalAlerts = result.newTotalAlerts;
      newTotalSnoozedAlerts = result.newTotalSnoozedAlerts;
      draft.triages[patientId] = entry;
    });
    if (newTotalAlerts !== null) {
      setTotalAlerts(newTotalAlerts);
    }
    if (newTotalSnoozedAlerts !== null) {
      setTotalSnoozedAlerts(newTotalSnoozedAlerts);
    }
    setTriageData(newTriageData);
    const snoozeWord = hasUnsnoozed ? 'unsnoozed' : 'snoozed';
    // Notify the user
    Toast({
      intent: 'success',
      message: `${patient.firstName} was ${snoozeWord} successfully!`
    });
  };

  /**
   * Rectify the effect of resolving the alerts
   *
   * @param alerts the alerts that have been resolved
   */
  const handleResolveAlert = (alerts: IAlert[]): void => {
    if (!alerts || !alerts.length) {
      return;
    }
    let newTotalSnoozedAlerts = null;
    let newTotalAlerts = null;
    let patientIds: number[] = [];
    for (const alert of alerts) {
      const entry = triageData.triages[alert.patientId];
      if (entry && entry.alertIds.includes(alert.id)) {
        patientIds.push(alert.patientId);
      }
    }
    // Refetch the relevant entries
    patientIds = uniq(patientIds);
    const promises: Promise<TriagedPatient>[] = [];
    for (const patientId of patientIds) {
      promises.push(GetTriagePatientData(patientId));
    }
    Promise.allSettled(promises).then((results) => {
      // Rectify the state
      const newTriageData = produce(triageData, (draft) => {
        for (const alert of alerts) {
          // Update the alert
          draft.alerts[alert.id] = alert;
          // If the alert is currently snoozed, update the snoozed data
          if (alert.snoozedUntil && alert.snoozedUntil > new Date()) {
            if (draft.pages?.snoozedAlerts) {
              draft.pages.snoozedAlerts.entityIds =
                draft.pages.snoozedAlerts.entityIds.filter(
                  (id) => id !== alert.id
                );
              draft.pages.snoozedAlerts.total =
                draft.pages.snoozedAlerts.total === 0
                  ? 0
                  : draft.pages.alerts.total - 1;
              newTotalSnoozedAlerts = produce(
                totalSnoozedAlerts,
                (totalSnoozedAlerts) => {
                  return totalSnoozedAlerts + 1;
                }
              );
            }
            // Otherwise, update the open alert data
          } else {
            if (draft.pages?.alerts) {
              draft.pages.alerts.entityIds =
                draft.pages.alerts.entityIds.filter((id) => id !== alert.id);
              draft.pages.alerts.total =
                draft.pages.alerts.total === 0
                  ? 0
                  : draft.pages.alerts.total - 1;
              newTotalAlerts = produce(totalAlerts, (totalAlerts) => {
                return totalAlerts === 0 ? 0 : totalAlerts - 1;
              });
            }
          }
          // Update the relevant triage entries to the new category
          for (const result of results) {
            if (result.status === 'fulfilled') {
              const entry = result.value;
              updateTriageEntry(draft, entry);
            }
          }
        }
      });
      if (newTotalAlerts != null) {
        setTotalAlerts(newTotalAlerts);
      }
      if (newTotalSnoozedAlerts != null) {
        setTotalSnoozedAlerts(newTotalSnoozedAlerts);
      }
      setTriageData(newTriageData);
    });
    // Notify the user
    Toast({
      intent: 'success',
      message: `The ${
        alerts.length === 1 ? 'alert was' : 'alerts were'
      } resolved successfully!`
    });
  };

  // Utilities

  const loadingCategories = getLoadingCategories(loadingState);

  const isCategoryLoading = (category: TriageLoadingKey): boolean => {
    if (!loadingCategories || !isLoading) {
      return false;
    }
    return loadingCategories.includes(category);
  };

  useEffect(() => {
    if (!triageData || !triageData.pages) {
      return;
    }

    const stats: TriageStats = {
      totalAlerts: totalAlerts ? totalAlerts : 0,
      totalSnoozedAlerts: totalSnoozedAlerts ? totalSnoozedAlerts : 0,
      totalStaleAlerts: staleAlertsData ? staleAlertsData.total : 0,
      triageAlerts: 0,
      danger: 0,
      warning: 0,
      success: 0,
      unresponsive: 0,
      inactive: 0,
      completed: 0,
      snoozed: 0,
      paused: 0,
      recent: 0,
      stun: 0
    };
    for (const category in triageData.pages) {
      const total = triageData.pages[category].total;
      if (category === 'alerts') {
        stats.triageAlerts = total;
      } else if (category === 'snoozedAlerts') {
        stats.stun = triageData.pages[category].stunTotal;
      } else {
        stats[category] = total;
      }
    }
    setStats(stats);
  }, [triageData, totalAlerts, totalSnoozedAlerts]);

  /**
   * Reconcile patients being viewed with
   * possible missing chats (since we
   * only initially fetch a subset of them
   * on initial app load)
   */
  const {
    threads,
    addThreadToContext,
    isLoading: chatsLoading,
    setTriagePatientIds
  } = useChats();
  useEffect(() => {
    if (!chatsLoading && !pgOrTriageLoading && triageData && triageData.pages) {
      const patientIdsForCurrentView = [
        triageData.pages?.danger?.entityIds,
        triageData.pages?.warning?.entityIds,
        triageData.pages?.success?.entityIds,
        triageData.pages?.unresponsive?.entityIds,
        triageData.pages?.inactive?.entityIds,
        triageData.pages?.snoozed?.entityIds,
        triageData.pages?.completed?.entityIds,
        triageData.pages?.paused?.entityIds,
        triageData.pages?.recent?.entityIds,
        triageData.pages?.empty?.entityIds
      ]
        .flat()
        .filter((ids) => ids);
      const uniquePatientIds = [...new Set(patientIdsForCurrentView)];
      const result = determineMissingChatThreads(uniquePatientIds, threads);
      getAndAddMissingThreads(result, addThreadToContext);
      if (triageData.patientIds) {
        setTriagePatientIds(triageData.patientIds);
      }
    }
  }, [chatsLoading, pgOrTriageLoading, triageData]);

  const stripAllAppFilters = (
    raw: TriageParamsOptions
  ): TriageParamsOptions => {
    const cloned: TriageParamsOptions = JSON.parse(JSON.stringify(raw));
    // Remove global temp filter
    delete (cloned as any)._tempAppFilter;
    // Remove patientIds apps scope if present
    if (cloned.patientIds && 'apps' in cloned.patientIds) {
      delete (cloned.patientIds as any).apps;
    }
    // Alerts groups
    if (cloned.alerts && 'apps' in cloned.alerts)
      delete (cloned.alerts as any).apps;
    if (cloned.snoozedAlerts && 'apps' in cloned.snoozedAlerts)
      delete (cloned.snoozedAlerts as any).apps;
    if (cloned.staleAlerts && 'apps' in cloned.staleAlerts)
      delete (cloned.staleAlerts as any).apps;
    // Patient categories
    if (cloned.patients) {
      for (const key of Object.keys(cloned.patients)) {
        if ('apps' in cloned.patients[key]) {
          delete (cloned.patients[key] as any).apps;
        }
      }
    }
    return cloned;
  };

  const value: TriageValue = {
    data: triageData,
    triageInProgress: triageData?.triageInProgress || false,
    setTriageData,
    addTriagedPatient,
    staleAlertsData: getStaleAlertsData(staleAlertsData, staleAlertsPage),
    options,
    updateOptions,
    tempAppFilter: options?._tempAppFilter,
    updateTempAppFilter,
    updateStaleAlertsPage: (page: number) => {
      setStaleAlertsPage(page);
      const newOptions = produce(options, (draft) => {
        draft.staleAlerts = draft.staleAlerts
          ? { ...draft.staleAlerts, page }
          : { page, limit: 10 };
      });
      updateOptions(newOptions);
    },
    stats,
    isLoading,
    loadingCategories,
    isCategoryLoading,
    isStaleAlertsLoading,
    isPendingPGEnterprise,
    error,
    getContact,
    handleResolveAlert,
    handleToggleSnoozeAlert,
    handleToggleSnoozePatient,
    resetToDashboard: () => {
      skipNextUrlSyncRef.current = true;
      navigate(location.pathname); // clear query
      const reset = stripAllAppFilters(DEFAULT_TRIAGE_FILTERS);
      // Set internal flag to prevent reinjection of app filters
      reset._forceAllApps = true;
      // Force all category orders back to defaults
      for (const cat of Object.keys(reset.patients)) {
        reset.patients[cat].order = ['alerts', 'desc'];
        reset.patients[cat].page = 0;
      }
      reset.alerts.order = 'desc';
      reset.alerts.page = 0;
      reset.snoozedAlerts.page = 0;
      reset.staleAlerts.page = 0;
      setOptions(reset);
      setRefreshTick((n) => n + 1);
    }
  };

  return (
    <TriageContext.Provider value={value}>
      <TriageUpdaterContext.Provider value={dispatch}>
        {children}
      </TriageUpdaterContext.Provider>
    </TriageContext.Provider>
  );
};

export function useTriage(): TriageValue {
  const context = React.useContext(TriageContext);
  if (context === undefined) {
    throw new Error('useTriage must be used within a TriageProvider');
  }
  return context;
}
